package com.evoapps.evoshift.domain.usecase.team

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.usecase.user.GetAssignableEmployeesUseCase
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Instant

data class TeamMemberStatus(
    val user: User,
    val currentShift: Shift? = null,
    val nextShift: Shift? = null,
    val isWorkingNow: Boolean = false
)

class GetTeamStatusUseCase(
    private val getAssignableEmployeesUseCase: GetAssignableEmployeesUseCase,
    private val shiftRepository: ShiftRepository,
    private val now: () -> Instant = { AppClock.now() },
    private val timeZone: () -> TimeZone = { TimeZone.currentSystemDefault() }
) {
    operator fun invoke(workplaceId: String, rosterId: String?): Flow<List<TeamMemberStatus>> = flow {
        // 1. Fetch Users
        // SH-168: a failed read fails the flow (the screen offers Try again), never an empty team.
        val users = getAssignableEmployeesUseCase(workplaceId).getOrThrow()

        if (users.isEmpty()) {
            emit(emptyList())
            return@flow
        }

        // 2. If no roster is active, return users with no shift status
        if (rosterId == null) {
            emit(users.map { TeamMemberStatus(user = it) })
            return@flow
        }

        // 3. Observe Shifts
        shiftRepository.observeShiftsForRoster(rosterId).collect { shifts ->
            // Shift times are local ISO date-times: compare with local now, not a UTC instant.
            val now = now().toLocalDateTime(timeZone()).toString()

            val statusList = users.map { user ->
                val userShifts = shifts.filter { it.userId == user.uid }

                val currentShift = userShifts.find { it.isOngoingAt(now) }

                val nextShift = userShifts
                    .filter { it != currentShift && it.plannedTime.start > now }
                    .minByOrNull { it.plannedTime.start }

                TeamMemberStatus(
                    user = user,
                    currentShift = currentShift,
                    nextShift = nextShift,
                    isWorkingNow = currentShift != null
                )
            }
            emit(statusList)
        }
    }
}
/** Clocked in and not out yet; without clock times, within the planned hours. */
internal fun Shift.isOngoingAt(now: String): Boolean {
    val actual = actualTime
    if (actual != null && actual.start.isNotEmpty()) return actual.end.isEmpty() || now < actual.end
    return plannedTime.start <= now && now < plannedTime.end
}
